/**
 * จัดกลุ่ม connected accounts ตาม platform สำหรับ /settings/integrations
 *
 * แยกออกมาจาก component เพราะนี่คือจุดที่บั๊กเดิมอยู่: PlatformAccountsGrid ใช้
 * Map<PlatformType, PlatformAccount> แล้ว .set() ทับกัน ทำให้เหลือ account เดียว
 * ต่อ platform — และเพราะ service เรียง created_at DESC ตัวที่รอดคือตัวเก่าสุด
 * แปลว่าการต่อ account ใหม่ดูเหมือนไม่ติด ทั้งที่ลง DB แล้ว
 *
 * @module components/settings/groupAccountsByPlatform
 */

import type { PlatformAccount, PlatformConfig, PlatformType } from "@/types";

export interface PlatformSectionData {
  platform: PlatformConfig;
  accounts: PlatformAccount[];
}

export interface GroupedIntegrations {
  /** platform ที่มีอย่างน้อย 1 account เรียงตามลำดับใน `platforms` */
  sections: PlatformSectionData[];
  /** platform ที่ยังไม่มี account เลย เรียงตามลำดับใน `platforms` */
  unconnected: PlatformConfig[];
}

export const groupAccountsByPlatform = (
  platforms: PlatformConfig[],
  accounts: PlatformAccount[]
): GroupedIntegrations => {
  const accountsFor = (type: PlatformType): PlatformAccount[] =>
    accounts
      // .filter คืน array ใหม่เสมอ .sort ต่อท้ายจึงไม่แตะ `accounts` ที่รับมา
      .filter((account) => account.platform === type)
      // เก่า→ใหม่ เพื่อให้ account ที่เพิ่งต่อ "ต่อท้าย" แถวเดิมไม่ขยับ — งานหลัก
      // ของหน้านี้คือการเพิ่ม (spec §1) ส่วน service เรียง DESC มา ซึ่งจะแทรก
      // ของใหม่บนสุดและดันทุกแถวลง
      //
      // `?? ""` ไม่ใช่ความระแวง: demo accounts ไม่มี createdAt จริง ๆ แม้ type
      // จะบอกว่ามี (demoDataService.ts ถูก cast ที่ Settings.tsx ตอน merge demo เข้ากับของจริง)
      // ถ้าไม่ใส่ localeCompare จะโยนบน undefined
      .sort((a, b) => (a.createdAt ?? "").localeCompare(b.createdAt ?? ""));

  // เดินจาก `platforms` ไม่ใช่จาก `accounts`: ลำดับ section จึงคงที่ตาม
  // AVAILABLE_PLATFORMS และ account ที่ platform ไม่รู้จัก (เช่น demo
  // 'google_display' ที่ไม่มีใน PlatformType) หายไปเองโดยไม่ต้อง filter แยก
  const sections = platforms
    .map((platform) => ({ platform, accounts: accountsFor(platform.type) }))
    .filter((section) => section.accounts.length > 0);

  const unconnected = platforms.filter(
    (platform) => accountsFor(platform.type).length === 0
  );

  return { sections, unconnected };
};

export default groupAccountsByPlatform;
